// One plain sentence describing a result, made from the spec and the total only. A copy of
// supabase/functions/ask/lib/summary.ts, used when a result is re-run in the browser (a chip removed, a sort); keep the
// two in step (apps/crm/src/lib/ask.test.ts checks they agree).

export interface SummaryFilter {
  field: string;
  op: string;
  value?: unknown;
}
export interface SummarySpec {
  source: string;
  filters?: SummaryFilter[];
  sort?: { field: string; dir?: string }[];
}

const NOUNS: Record<string, [string, string]> = {
  firms: ["firm", "firms"],
  people: ["person", "people"],
  activity: ["activity item", "activity items"],
  deals: ["deal", "deals"],
  meetings: ["meeting", "meetings"],
  events: ["event", "events"],
  industry_events: ["industry event", "industry events"],
  networks: ["network", "networks"],
  partners: ["partner", "partners"],
};

const KINDS: Record<string, [string, string]> = {
  target: ["prospect", "prospects"],
  referrer: ["referrer", "referrers"],
  investor: ["investor", "investors"],
  lender: ["lender", "lenders"],
  customer: ["customer", "customers"],
  partner: ["partner", "partners"],
};

/** Field names as words: last_contacted_at -> "last contacted", fit_score -> "fit score". */
export function fieldLabel(field: string): string {
  const special: Record<string, string> = {
    at: "date", firm: "firm", type: "type", main_contact: "main contact", found_by: "source", stage_key: "stage",
    size_estimate: "size estimate", last_reply_intent: "last reply", firm_stage: "firm stage", starts_at: "start",
  };
  if (special[field]) return special[field]!;
  return field.replace(/_at$/, "").replace(/_/g, " ");
}

function words(v: unknown): string {
  if (Array.isArray(v)) {
    const parts = v.map(words);
    return parts.length > 1 ? `${parts.slice(0, -1).join(", ")} or ${parts[parts.length - 1]}` : (parts[0] ?? "");
  }
  return String(v ?? "").replace(/_/g, " ");
}

const PLACE_FIELDS = ["city", "country", "region", "nation"];

function phrase(f: SummaryFilter): { text: string; place: boolean } {
  const label = fieldLabel(f.field);
  const v = words(f.value);
  const days = Number(f.value);
  if (PLACE_FIELDS.includes(f.field) && (f.op === "eq" || f.op === "contains" || f.op === "in" || f.op === "starts_with")) {
    const place = v === v.toLowerCase() ? v.replace(/\b\w/g, (c) => c.toUpperCase()) : v;
    return { text: `in ${place}`, place: true };
  }
  switch (`${f.field}:${f.op}`) {
    case "contacted:is_true": return { text: "that you've contacted", place: false };
    case "contacted:is_false": return { text: "that you haven't contacted", place: false };
    case "replied:is_true": return { text: "that have replied", place: false };
    case "replied:is_false": return { text: "that haven't replied", place: false };
    case "in_sequence:is_true": return { text: "in a sequence", place: false };
    case "in_sequence:is_false": return { text: "not in a sequence", place: false };
    case "closed:is_true": return { text: "that are closed", place: false };
    case "closed:is_false": return { text: "that are open", place: false };
    case "main_contact:is_true": return { text: "who are the main contact", place: false };
    case "last_contacted_at:within_days": return { text: `contacted in the last ${days} days`, place: false };
    case "last_contacted_at:older_than_days": return { text: `last contacted more than ${days} days ago`, place: false };
    case "first_contacted_at:within_days": return { text: `first contacted in the last ${days} days`, place: false };
    case "last_reply_at:within_days": return { text: `that replied in the last ${days} days`, place: false };
    case "last_reply_at:older_than_days": return { text: `that last replied more than ${days} days ago`, place: false };
    case "created_at:within_days": return { text: `added in the last ${days} days`, place: false };
    case "at:within_days": return { text: `in the last ${days} days`, place: false };
    case "starts_at:within_days": return { text: `in the last ${days} days`, place: false };
    case "stage_key:eq":
      if (f.value === "meeting_booked") return { text: "with a meeting booked", place: false };
      return { text: `at ${v}`, place: false };
    case "stage:eq": case "firm_stage:eq":
    case "stage:contains": case "firm_stage:contains": return { text: `at ${v}`, place: false };
    case "type:eq": return { text: `of type ${v}`, place: false };
    case "segment:eq": case "segment:contains": return { text: `in the ${v} segment`, place: false };
  }
  switch (f.op) {
    case "eq": return { text: `with ${label} ${v}`, place: false };
    case "neq": return { text: `with ${label} not ${v}`, place: false };
    case "contains": return { text: `with ${label} containing "${v}"`, place: false };
    case "not_contains": return { text: `with ${label} not containing "${v}"`, place: false };
    case "starts_with": return { text: `with ${label} starting "${v}"`, place: false };
    case "gt": return { text: `with ${label} over ${v}`, place: false };
    case "gte": return { text: `with ${label} of at least ${v}`, place: false };
    case "lt": return { text: `with ${label} under ${v}`, place: false };
    case "lte": return { text: `with ${label} of at most ${v}`, place: false };
    case "in": return { text: `with ${label} ${v}`, place: false };
    case "is_true": return { text: `that are ${label}`, place: false };
    case "is_false": return { text: `that are not ${label}`, place: false };
    case "is_null": return { text: `with no ${label}`, place: false };
    case "not_null": return { text: `with a ${label}`, place: false };
    case "within_days": return { text: `with ${label} in the last ${days} days`, place: false };
    case "older_than_days": return { text: `with ${label} more than ${days} days ago`, place: false };
    case "before": return { text: `with ${label} before ${v}`, place: false };
    case "after": return { text: `with ${label} after ${v}`, place: false };
  }
  return { text: `with ${label} ${f.op} ${v}`, place: false };
}

function sortPhrase(s: { field: string; dir?: string }): string {
  const desc = s.dir === "desc";
  const special: Record<string, [string, string]> = {
    last_contacted_at: ["oldest contact first", "newest contact first"],
    first_contacted_at: ["earliest first contact first", "latest first contact first"],
    last_reply_at: ["oldest reply first", "newest reply first"],
    at: ["oldest first", "newest first"],
    created_at: ["oldest added first", "newest added first"],
    starts_at: ["soonest first", "latest first"],
    next_action_at: ["soonest next action first", "latest next action first"],
    next_step_at: ["soonest next step first", "latest next step first"],
    name: ["A to Z", "Z to A"],
    firm: ["by firm, A to Z", "by firm, Z to A"],
  };
  const sp = special[s.field];
  if (sp) return desc ? sp[1] : sp[0];
  const label = fieldLabel(s.field);
  return desc ? `highest ${label} first` : `lowest ${label} first`;
}

function join(parts: string[]): string {
  if (parts.length <= 1) return parts[0] ?? "";
  return `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;
}

/** "12 firms in Glasgow that you've contacted, newest contact first". */
export function summarise(spec: SummarySpec, total: number): string {
  let [one, many] = NOUNS[spec.source] ?? ["row", "rows"];
  let filters = spec.filters ?? [];
  // Activity reads better as what it is: "replies" or "sends".
  const dir = spec.source === "activity" ? filters.find((f) => f.field === "direction" && f.op === "eq") : undefined;
  if (dir && (dir.value === "received" || dir.value === "sent")) {
    [one, many] = dir.value === "received" ? ["reply", "replies"] : ["send", "sends"];
    filters = filters.filter((f) => f !== dir);
  }
  // Firms of one kind read as that kind: "prospects", "referrers".
  const kind = spec.source === "firms" ? filters.find((f) => f.field === "type" && f.op === "eq" && typeof f.value === "string" && KINDS[f.value]) : undefined;
  if (kind) {
    [one, many] = KINDS[String(kind.value)]!;
    filters = filters.filter((f) => f !== kind);
  }
  const phrases = filters.map(phrase);
  const places = phrases.filter((p) => p.place).map((p) => p.text);
  const rest = phrases.filter((p) => !p.place).map((p) => p.text);
  const head = total === 0 ? `No ${many}` : `${total.toLocaleString("en-GB")} ${total === 1 ? one : many}`;
  let s = [head, places.join(" "), join(rest)].filter(Boolean).join(" ");
  const sort = spec.sort?.[0];
  if (sort) s += `, ${sortPhrase(sort)}`;
  return s;
}
